import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../api/client.js";
import {
  compressImageForUpload,
  MAX_UPLOAD_BYTES,
  UPLOAD_IMAGE_MIMES,
  type UploadImageMime,
} from "../util/imageCompression.js";
import type { PropertyInsights } from "./insights-api.js";

/** Property API contract — kept in lockstep with /customer/api/src/domain/property.ts. */
export type PropertyType =
  | "single_family"
  | "multi_family"
  | "condo"
  | "townhouse"
  | "commercial"
  | "other";

export const propertyTypeLabels: Record<PropertyType, string> = {
  single_family: "Single-family home",
  multi_family: "Multi-family",
  condo: "Condo",
  townhouse: "Townhouse",
  commercial: "Commercial",
  other: "Other",
};

/**
 * Customer-chosen display icon. Three kinds:
 *   - preset: a built-in glyph rendered client-side (no URL).
 *   - photo:  a customer image; `value` is an S3 key under this property's
 *             own icon/ or binder/ prefix.
 *   - aerial: the stored NAIP aerial view (key resolved server-side).
 */
export const PRESET_PROPERTY_ICONS = [
  "house",
  "apartment",
  "condo",
  "townhouse",
  "cabin",
  "building",
  "yard",
  "farm",
  "workshop",
  "storefront",
] as const;
export type PresetPropertyIcon = (typeof PRESET_PROPERTY_ICONS)[number];

export type PropertyIcon =
  | { kind: "preset"; value: PresetPropertyIcon }
  | { kind: "photo"; value: string }
  | { kind: "aerial" };

/** MIME types accepted for icon photo uploads — mirrors binder media. */
export const ICON_CONTENT_TYPES = UPLOAD_IMAGE_MIMES;
export type IconContentType = UploadImageMime;

/** Backstop cap — photos are downscaled client-side first. */
export const MAX_ICON_BYTES = MAX_UPLOAD_BYTES;

export interface Property {
  id: string;
  ownerSub: string;
  nickname: string;
  propertyType: PropertyType;
  street: string;
  unit?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  notes?: string;
  /** Customer-chosen display icon; absent = client-side default by type. */
  icon?: PropertyIcon;
  /**
   * Short-lived presigned display URL (~5 min) — always present for photo
   * icons, present for aerial icons once the aerial crop exists. Presets
   * render client-side and never carry one.
   */
  iconUrl?: string;
  createdAt: string;
  updatedAt: string;
  /**
   * Auto-detected property facts — DETAIL responses only (additive key on
   * GET /properties/{id}). `null` means not enriched yet; absent on list items.
   */
  insights?: PropertyInsights | null;
}

export interface PropertyInput {
  nickname: string;
  propertyType: PropertyType;
  street: string;
  unit?: string;
  city: string;
  state: string;
  postalCode: string;
  country?: string;
  notes?: string;
}

export const QK = {
  list: ["properties"] as const,
  detail: (id: string) => ["properties", id] as const,
};

export function usePropertiesList() {
  return useQuery({
    queryKey: QK.list,
    queryFn: async () => {
      const res = await apiFetch<{ items: Property[] }>("/properties");
      return res.items;
    },
  });
}

export function useProperty(id: string | undefined) {
  return useQuery({
    queryKey: id ? QK.detail(id) : ["properties", "noop"],
    queryFn: () => apiFetch<Property>(`/properties/${id!}`),
    enabled: !!id,
  });
}

export function useCreateProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PropertyInput) =>
      apiFetch<Property>("/properties", { method: "POST", json: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.list });
    },
  });
}

export function useUpdateProperty(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<PropertyInput>) =>
      apiFetch<Property>(`/properties/${id}`, { method: "PATCH", json: input }),
    onSuccess: (updated) => {
      // The PATCH response is the bare record — it carries neither the
      // `insights` ride-along nor the transient `iconUrl`. Carry those over
      // from the cached detail so seeding the cache doesn't strip them.
      qc.setQueryData<Property>(QK.detail(id), (prev) =>
        prev ? { ...updated, insights: prev.insights, iconUrl: prev.iconUrl } : updated,
      );
      void qc.invalidateQueries({ queryKey: QK.list });
    },
  });
}

export function useDeleteProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<unknown>(`/properties/${id}`, { method: "DELETE" }),
    onSuccess: (_data, id) => {
      qc.removeQueries({ queryKey: QK.detail(id) });
      void qc.invalidateQueries({ queryKey: QK.list });
    },
  });
}

// ---- Icon ------------------------------------------------------------------

/**
 * Set (or clear, with `null`) the property's display icon. The PATCH response
 * carries neither `iconUrl` nor `insights`, so we refetch the detail and list
 * instead of writing the response into the cache.
 */
export function useSetPropertyIcon(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (icon: PropertyIcon | null) =>
      apiFetch<Property>(`/properties/${id}`, { method: "PATCH", json: { icon } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.detail(id) });
      void qc.invalidateQueries({ queryKey: QK.list });
    },
  });
}

export interface PresignIconUploadResponse {
  key: string;
  contentType: IconContentType;
  uploadUrl: string;
  expiresInSeconds: number;
}

/** POST /properties/{id}/icon-presign — the SERVER picks the S3 object key. */
export function presignIconUpload(
  propertyId: string,
  contentType: IconContentType,
): Promise<PresignIconUploadResponse> {
  return apiFetch<PresignIconUploadResponse>(`/properties/${propertyId}/icon-presign`, {
    method: "POST",
    json: { contentType },
  });
}

/** Thrown when the caller aborts an in-flight icon upload (dialog closed). */
export class UploadCancelledError extends Error {
  constructor() {
    super("Upload cancelled");
    this.name = "UploadCancelledError";
  }
}

/**
 * Presign → upload-to-S3 for a new icon photo, mirroring uploadBinderMedia.
 * Returns the S3 key to PATCH back as `{ icon: { kind: "photo", value: key } }`
 * (the PATCH re-verifies the uploaded object server-side).
 */
export async function uploadPropertyIconPhoto(args: {
  propertyId: string;
  file: File;
  contentType: IconContentType;
  onProgress?: (loaded: number, total: number) => void;
  /** Abort mid-flight (e.g. the picker dialog closed) — rejects with UploadCancelledError. */
  signal?: AbortSignal;
}): Promise<string> {
  if (args.signal?.aborted) throw new UploadCancelledError();
  // Before presign: compression can change the content type, and the server
  // signs the PUT against it.
  const { file, contentType } = await compressImageForUpload(args.file, args.contentType, {
    signal: args.signal,
  });
  if (args.signal?.aborted) throw new UploadCancelledError();
  const presign = await presignIconUpload(args.propertyId, contentType);
  if (args.signal?.aborted) throw new UploadCancelledError();

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const onAbort = () => xhr.abort();
    args.signal?.addEventListener("abort", onAbort, { once: true });
    const done = (fn: () => void) => {
      args.signal?.removeEventListener("abort", onAbort);
      fn();
    };
    xhr.open("PUT", presign.uploadUrl);
    xhr.setRequestHeader("Content-Type", presign.contentType);
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) args.onProgress?.(ev.loaded, ev.total);
    };
    xhr.onload = () =>
      done(() =>
        xhr.status >= 200 && xhr.status < 300
          ? resolve()
          : reject(new Error(`S3 upload failed: HTTP ${xhr.status}`)),
      );
    xhr.onabort = () => done(() => reject(new UploadCancelledError()));
    xhr.onerror = () => done(() => reject(new Error("S3 upload network error")));
    xhr.send(file);
  });

  return presign.key;
}
